// The plain-English renderer (RULES_SCHEMA.md §18): LeagueSettings in, short
// sentences out. One document feeds the rules page, the wizard summary, and
// the "rules changed" feed post. Sentence templates sit next to the field
// they describe; nothing here knows a sport by name. Stat, slot, and
// position names come from the SportDefinition.

import { z } from 'zod';
import type { SportDefinition } from '@fsp/sports-core';
import type {
  BonusRule,
  DraftOrder,
  LeagueSettings,
  TierRule,
  TiebreakerSettings,
  UnitRule,
} from './schemas';
import { reserveStatusList } from '../roster/reserve';
import { describeTaxi, taxiDeadlineWeek } from '../roster/taxi';
import { playsTheField, playsTheMedian, playsTwoOpponents } from '../matchup/format';
import { effectiveTools } from '../tools/tools';
import { scoringTable, startingPositions } from './scoring-table';

/**
 * ZOD, NOT AN INTERFACE, since 2026-09-17.
 *
 * This document crosses the wire — `POST /api/leagues/preview` serves it so a
 * client with no rules engine can render the same sentences the wizard does
 * (audit 3.6) — and every shape that crosses the wire has to be generatable
 * into `docs/design/contracts/schema/`. The type is inferred from the schema
 * so the two cannot drift.
 */
export const RulesSectionId = z.enum([
  'league',
  'format',
  'scoring',
  'roster',
  'lineup',
  'schedule',
  'playoffs',
  'tiebreakers',
  'waivers',
  'trades',
  'draft',
  'keepers',
  'governance',
  'fun',
  'tools',
]);
export type RulesSectionId = z.infer<typeof RulesSectionId>;

export const RulesSection = z.object({
  id: RulesSectionId,
  title: z.string(),
  sentences: z.array(z.string()),
});
export type RulesSection = z.infer<typeof RulesSection>;

export const RulesDocument = z.object({
  /** "PPR" or "Custom, based on PPR". */
  headline: z.string(),
  sections: z.array(RulesSection),
});
export type RulesDocument = z.infer<typeof RulesDocument>;

/**
 * A format for somebody who has never played it (B-429): its short name, one
 * line that is the whole idea, and a few labeled lines saying what a week is,
 * who wins, and the one thing worth knowing. The create form shows them on the
 * format step, and `GET /api/sports` serves them so a second client does not
 * write its own.
 *
 * They describe the format as a new league gets it (`buildDefaultSettings`):
 * the formats with a bracket say so and the others say they have none, which
 * the describe test holds to the defaults. The rules page describes a league's
 * own rules from its settings, not from these.
 */
export interface FormatWords {
  /** "Head-to-head", for a chip, a tab or a card's name. */
  label: string;
  /** "Play one team each week. The higher score wins." */
  tagline: string;
  /** "Each week", "Who wins", "Good to know", each with its plain sentences. */
  explainer: Array<{ label: string; text: string }>;
}

export function formatWords(id: LeagueSettings['format']['id']): FormatWords {
  return FORMAT_WORDS[id];
}

const PLAYOFFS_DECIDE =
  'Wins and losses make the standings, and the top teams meet in the playoffs to decide the champion.';

const FORMAT_WORDS: Record<LeagueSettings['format']['id'], FormatWords> = {
  'h2h-points': {
    label: 'Head-to-head',
    tagline: 'Play one team each week. The higher score wins.',
    explainer: [
      {
        label: 'Each week',
        text: 'You’re matched up with one other team in the league. Your starters’ points are added up, theirs are too, and the higher total wins the game.',
      },
      {
        label: 'Who wins',
        text: 'Wins and losses make the standings, like a real sports league, and the top teams meet in the playoffs to decide the champion.',
      },
      {
        label: 'Good to know',
        text: 'The way most leagues play. A big week can still lose to an opponent who had a bigger one.',
      },
    ],
  },
  'all-play': {
    label: 'All-play',
    tagline: 'Play every team, every week.',
    explainer: [
      {
        label: 'Each week',
        text: 'Your score is lined up against every other team’s. Beat 7 of the other 9 and your week counts as 7 wins and 2 losses.',
      },
      { label: 'Who wins', text: PLAYOFFS_DECIDE },
      {
        label: 'Good to know',
        text: 'Nobody wins or loses on who they happened to face: a high score always pays off.',
      },
    ],
  },
  'best-ball': {
    label: 'Best ball',
    tagline: 'Draft a team, then never set a lineup.',
    explainer: [
      {
        label: 'Each week',
        text: 'You play one other team, as in head-to-head. After the games, your top scorers fill your lineup on their own, so no points are left on your bench.',
      },
      { label: 'Who wins', text: PLAYOFFS_DECIDE },
      {
        label: 'Good to know',
        text: 'Made for people who would rather not check in every week. The draft matters most, though you can still pick up and trade players.',
      },
    ],
  },
  'points-total': {
    label: 'Total points',
    tagline: 'No opponents. The most points over the season wins.',
    explainer: [
      {
        label: 'Each week',
        text: 'Your starters’ points are added to your season total. There are no games, so no wins or losses.',
      },
      {
        label: 'Who wins',
        text: 'The team with the most points after the last week is the champion. There are no playoffs.',
      },
      {
        label: 'Good to know',
        text: 'Simple to follow: the standings show how far each team is behind the leader, and every week is a chance to catch up.',
      },
    ],
  },
  elimination: {
    label: 'Elimination',
    tagline: 'The lowest score each week is knocked out.',
    explainer: [
      {
        label: 'Each week',
        text: 'Every team still in plays against the whole league. The lowest score is out for the rest of the season, and its players become free for the teams still in to pick up.',
      },
      {
        label: 'Who wins',
        text: 'The last team left is the champion. If the season runs out first, the team still in with the most points wins. There are no playoffs.',
      },
      {
        label: 'Good to know',
        text: 'Every week counts, even for the leaders: one bad week ends a season. A team that is out can still follow along and chat.',
      },
    ],
  },
  'h2h-categories': {
    label: 'Categories',
    tagline: 'Play one team each week, category by category.',
    explainer: [
      {
        label: 'Each week',
        text: 'You play one other team across several stat categories. Win more categories than they do and you win the week.',
      },
      { label: 'Who wins', text: PLAYOFFS_DECIDE },
      {
        label: 'Good to know',
        text: 'Every kind of stat matters, not just one total.',
      },
    ],
  },
  'h2h-each-category': {
    label: 'Each category',
    tagline: 'Every category is its own win or loss.',
    explainer: [
      {
        label: 'Each week',
        text: 'You play one other team across several stat categories, and each one counts on its own: win 6 of 10 and your week is 6 wins and 4 losses.',
      },
      { label: 'Who wins', text: PLAYOFFS_DECIDE },
      {
        label: 'Good to know',
        text: 'A close week reads as close, instead of all or nothing.',
      },
    ],
  },
  rotisserie: {
    label: 'Rotisserie',
    tagline: 'No opponents. Rank high in every category all season.',
    explainer: [
      {
        label: 'Each week',
        text: 'Your stats add up all season in each category, and the teams are ranked category by category.',
      },
      {
        label: 'Who wins',
        text: 'Your ranks across the categories add up to your score, and the highest score when the season ends is the champion. There are no playoffs.',
      },
      {
        label: 'Good to know',
        text: 'Every category counts the same, so a team strong at everything does well.',
      },
    ],
  },
};

/**
 * The short name for a format, for a chip or a tab.
 *
 * MOVED OUT OF THE WEB APP on 2026-09-17. It was a `Record<LeagueFormatId,
 * string>` in `LeagueSetupScreen.tsx`, which meant a second client would
 * either show raw ids or transcribe these eight strings — and the long
 * sentence below is the same fact said at length, so the two could disagree
 * about what a format is called. `GET /api/sports` serves both. Read from the
 * format's words since 2026-10-18, so the card and the chip share one name.
 */
export const FORMAT_LABELS = Object.fromEntries(
  Object.entries(FORMAT_WORDS).map(([id, words]) => [id, words.label]),
) as Record<LeagueSettings['format']['id'], string>;

/**
 * What the game-time lock does, in the league's own schedule: the rules
 * page's sentences when it is on, and what the Settings switch says it turns
 * on. One answer for both, because they are two readings of one field — the
 * switch still said "Players lock for the period once their game starts" after
 * W3 rewrote the rules page, and on a schedule that stopped being true for a
 * free agent (the W3 review, 2026-09-24).
 *
 * On a weekly or daily schedule a started player cannot be dropped and a
 * started free agent waits on waivers for the run after the period
 * (PLAN-2026-09-24 §2 rule 6, B-142). With no run to wait for — no waivers, or
 * a continuous schedule — both are locked until the period ends.
 */
export function gameTimeLockSentences(waivers: LeagueSettings['waivers']): string[] {
  if (waivers.system === 'none' || waivers.processing.schedule === 'continuous')
    return ['Players lock for the week once their game starts.'];
  return [
    'A player whose game has started cannot be dropped until the week ends.',
    'A free agent whose game has started is on waivers until claims process after the week.',
  ];
}

/**
 * A pick order in words: what it is called on a form, how a draft page names
 * it in a line, and what it does. One answer for the create form, the draft
 * page and the offline entry, so they cannot disagree about
 * which way round three runs.
 */
export function draftOrderWords(order: DraftOrder): {
  label: string;
  inLine: string;
  sentence: string;
} {
  return ORDER_WORDS[order];
}

const ORDER_WORDS: Record<DraftOrder, { label: string; inLine: string; sentence: string }> = {
  snake: {
    label: 'Snake',
    inLine: 'snake order',
    sentence:
      'The order turns around every round: the last pick of one round is the first of the next.',
  },
  linear: {
    label: 'Linear',
    inLine: 'linear order',
    sentence: 'Every round runs in the same order, so the first pick is first every round.',
  },
  'third-round-reversal': {
    label: 'Third-round reversal',
    inLine: 'third-round reversal',
    sentence:
      'A snake, except round three runs the same way as round two: the last pick of round one is the first of rounds two and three, which evens out the first pick’s head start.',
  },
};

/** The sentence that says what a format means. */
export function formatDescription(id: LeagueSettings['format']['id']): string {
  return FORMAT_NAMES[id];
}

const FORMAT_NAMES: Record<LeagueSettings['format']['id'], string> = {
  'h2h-points': 'Head-to-head, most points wins each matchup',
  'h2h-categories': 'Head-to-head, most categories won takes the matchup',
  'h2h-each-category': 'Head-to-head, every category is its own win or loss',
  rotisserie: 'Rotisserie: ranked in every category all season',
  'points-total': 'Total points all season, no matchups',
  'all-play': 'All-play: every team plays every team every week',
  'best-ball': 'Best ball: your best lineup is picked for you after the games',
  elimination: 'Elimination: the lowest score each week is out',
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TIEBREAKER_NAMES: Record<TiebreakerSettings['standings'][number], string> = {
  'head-to-head': 'head-to-head record',
  'points-for': 'points scored',
  'points-against': 'points allowed',
  'division-record': 'division record',
  'record-vs-common': 'record against common opponents',
  'all-play-record': 'all-play record',
  'points-for-last-3': 'points scored over the last three weeks',
  'coin-flip': 'a coin flip',
};

function n(x: number): string {
  return Number.isInteger(x) ? String(x) : String(Math.round(x * 100) / 100);
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

function list(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function clock(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour} ${suffix}` : `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

function statName(sport: SportDefinition, key: string, lower = true): string {
  const def = sport.stats.find((s) => s.key === key);
  const name = def?.name ?? key;
  return lower ? name.toLowerCase() : name;
}

function positionNames(sport: SportDefinition, ids: readonly string[] | null): string | null {
  if (!ids || ids.length === 0) return null;
  const names = ids.map((id) => {
    const p = sport.positions.find((x) => x.id === id);
    return p ? `${p.name.toLowerCase()}s` : id;
  });
  return list(names);
}

/**
 * A league's IR in the rules' words: "1 IR spot for players listed as out,
 * injured reserve or suspended. A player there who no longer qualifies has to
 * come off it before the team adds anyone." The Rules page and the lineup's
 * IR band say the same sentence (D9).
 */
export function describeReserve(
  sport: SportDefinition,
  reserve: NonNullable<LeagueSettings['roster']['reserve']>,
): string {
  const statuses = reserveStatusList(
    reserve,
    (id) => sport.injuryStatuses.find((st) => st.id === id)?.name ?? id,
  );
  const leave = reserve.mustMoveWhenHealthy
    ? ' A player there who no longer qualifies has to come off it before the team adds anyone.'
    : '';
  return `${plural(reserve.count, `${slotName(sport, reserve.slot)} spot`)} for players listed as ${statuses}.${leave}`;
}

function slotName(sport: SportDefinition, id: string): string {
  return sport.slots.find((s) => s.id === id)?.name ?? id;
}

/** "Passing Yards" in the passing group → "yard"; "Field Goals 40–49" → "field goal 40–49". */
function unitNoun(sport: SportDefinition, key: string, category: string): string {
  const name = statName(sport, key);
  const words = name.split(' ').map(singular);
  if (words[0] === category.toLowerCase() && words.length > 1) words.shift();
  return words.join(' ');
}

function signed(points: number): string {
  return points < 0 ? `−${n(Math.abs(points))}` : n(points);
}

// ── The scoring in the table's words (PLAN-2026-10-18 chunk 5) ────────────
//
// A sport that says its stats plainly (`StatDef.scoring`) has its scoring
// said the way its table says it: "Passing: touchdown pass 4; every 25
// passing yards 1; interception thrown −2", grouped by its kinds of play.
// The versions list, the feed and a vote say a change in these sentences, so
// they read the way the table shows it. A sport without the words keeps the
// catalog's ("0.04 per yard").

/** "every 25 passing yards 1", "touchdown pass 4", or null for a stat with no words. */
function plainUnit(sport: SportDefinition, u: UnitRule): string | null {
  const w = sport.stats.find((s) => s.key === u.stat)?.scoring;
  if (!w) return null;
  const unit = u.per !== 1 ? u.per : (w.unit ?? 1);
  const points = u.per !== 1 ? u.points : u.points * unit;
  const what = unit === 1 ? w.label.toLowerCase() : `every ${n(unit)} ${w.plural}`;
  const rounding =
    u.per !== 1 && u.rounding === 'floor'
      ? ', whole ones only'
      : u.per !== 1 && u.rounding === 'round'
        ? ', rounded'
        : '';
  return `${what}${rounding} ${signed(points)}`;
}

/** The kind of play a stat is said under: the layout's group, else its category. */
function playOf(sport: SportDefinition, key: string): string {
  const group = sport.scoringLayout?.groups.find((g) => g.stats.includes(key));
  if (group) return group.title;
  return capitalize(sport.stats.find((s) => s.key === key)?.category ?? 'other');
}

function plainUnitSentences(sport: SportDefinition, units: readonly UnitRule[]): string[] | null {
  if (!units.every((u) => plainUnit(sport, u) !== null)) return null;
  const order =
    sport.scoringLayout?.groups.flatMap((g) => g.stats) ?? sport.stats.map((s) => s.key);
  const sorted = [...units].sort((a, b) => order.indexOf(a.stat) - order.indexOf(b.stat));
  const groups = new Map<string, string[]>();
  for (const u of sorted.filter((x) => !x.positions || x.positions.length === 0)) {
    const play = playOf(sport, u.stat);
    groups.set(play, [...(groups.get(play) ?? []), plainUnit(sport, u)!]);
  }
  const out = [...groups].map(([play, parts]) => `${play}: ${parts.join('; ')}.`);
  for (const u of sorted.filter((x) => x.positions && x.positions.length > 0)) {
    const who = capitalize(positionNames(sport, u.positions) ?? 'some positions');
    out.push(`${who}: ${plainUnit(sport, u)!}.`);
  }
  return out;
}

function plainBonusSentence(sport: SportDefinition, b: BonusRule): string | null {
  const w = sport.stats.find((s) => s.key === b.stat)?.scoring;
  if (!w) return null;
  const who = positionNames(sport, b.positions);
  const scope = b.scope === 'game' ? 'in a game' : 'in a week';
  return `${n(b.threshold)} ${w.plural} ${scope} is worth ${plural(b.points, 'extra point')}${who ? ` for ${who}` : ''}.`;
}

function plainTierSentence(sport: SportDefinition, t: TierRule): string | null {
  const w = sport.stats.find((s) => s.key === t.stat)?.scoring;
  if (!w?.bands) return null;
  const noun = w.bands.noun;
  const bands = t.bands.map((band) => {
    const label =
      band.min === 0 && band.max === 0 && w.bands!.zero
        ? w.bands!.zero.toLowerCase()
        : band.max === null
          ? `${n(band.min)} or more ${noun}`
          : band.min === band.max
            ? `${n(band.min)} ${noun}`
            : `${n(band.min)}–${n(band.max)} ${noun}`;
    return `${label} ${signed(band.points)}`;
  });
  return `${w.label}${t.scope === 'period' ? ' in a week' : ''}: ${bands.join('; ')}.`;
}

/**
 * One sentence per stat group ("Passing: 0.04 per yard, 4 per touchdown, −2
 * per interception thrown."), then one per position-specific rule ("Tight
 * ends get 1.5 per reception."). Thirty unit rules read as six lines.
 */
function unitSentences(sport: SportDefinition, units: readonly UnitRule[]): string[] {
  const out: string[] = [];
  const groups = new Map<string, string[]>();
  for (const u of units.filter((x) => !x.positions || x.positions.length === 0)) {
    const category = sport.stats.find((s) => s.key === u.stat)?.category ?? 'other';
    const noun = unitNoun(sport, u.stat, category);
    const per = u.per === 1 ? `per ${noun}` : `per ${n(u.per)} ${noun}s`;
    const g = groups.get(category) ?? [];
    g.push(`${signed(u.points)} ${per}`);
    groups.set(category, g);
  }
  for (const [category, parts] of groups) out.push(`${capitalize(category)}: ${parts.join(', ')}.`);
  for (const u of units.filter((x) => x.positions && x.positions.length > 0)) {
    const who = capitalize(positionNames(sport, u.positions) ?? 'some positions');
    const category = sport.stats.find((s) => s.key === u.stat)?.category ?? 'other';
    const noun = unitNoun(sport, u.stat, category);
    out.push(
      `${who} ${u.points < 0 ? 'lose' : 'get'} ${n(Math.abs(u.points))} per ${noun}${u.per === 1 ? '' : ` (every ${n(u.per)})`}.`,
    );
  }
  return out;
}

function bonusSentence(sport: SportDefinition, b: BonusRule): string {
  const who = positionNames(sport, b.positions);
  const stat = statName(sport, b.stat);
  const scope = b.scope === 'game' ? 'in a game' : 'in a week';
  return `${n(b.threshold)} ${stat} ${scope} is worth ${plural(b.points, 'extra point')}${who ? ` for ${who}` : ''}.`;
}

function tierSentence(sport: SportDefinition, t: TierRule): string {
  const stat = statName(sport, t.stat);
  const bands = t.bands.map((band) => {
    const range =
      band.max === null
        ? `${n(band.min)} or more`
        : band.min === band.max
          ? n(band.min)
          : `${n(band.min)} to ${n(band.max)}`;
    return `${range}: ${n(band.points)}`;
  });
  return `${capitalize(stat)} scores by band (${bands.join('; ')}).`;
}

function singular(word: string): string {
  // Stats are named in the plural: "receptions" → "reception", "safeties" → "safety".
  if (word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * What a league's divisions say, as the Schedule and the Playoffs say it: the
 * divisions by name, and division winners first when they are seeded so. Null
 * where there is nothing to say. League settings' Divisions section reads the
 * same two (PLAN-2026-10-29-divisions §5).
 */
export function divisionSentences(s: LeagueSettings): {
  schedule: string | null;
  playoffs: string | null;
} {
  const divisions = s.schedule.divisions;
  if (divisions.length === 0) return { schedule: null, playoffs: null };
  // A FORMAT THAT PLAYS THE WHOLE LEAGUE has no division games to put first.
  const first = s.schedule.divisionGamesFirst && !playsTheField(s.format.id);
  return {
    schedule: `${plural(divisions.length, 'division')}: ${list(divisions.map((d) => d.name))}${first ? '; division games are scheduled first' : ''}.`,
    playoffs:
      s.playoffs.teamCount > 0 && s.playoffs.seeding === 'division-winners-first'
        ? 'Division winners take the top seeds.'
        : null,
  };
}

export function describeSettings(settings: LeagueSettings, sport: SportDefinition): RulesDocument {
  const s = settings;
  // THE SCORING'S NAME IS THE TABLE'S (PLAN-2026-10-18 §2.7): the setup's
  // own words for the usual numbers, or "Custom scoring", never a preset's
  // name, so nothing on screen says PPR.
  const table = scoringTable(s.scoring, sport, {
    positions: startingPositions(s.roster, sport),
  });
  const headline = table.headline;

  const sections: RulesSection[] = [];

  sections.push({
    id: 'league',
    title: 'League',
    sentences: [
      `${s.identity.name}: ${plural(s.identity.teamCount, 'team')}, ${sport.name}, ${s.season} season.`,
      // The rules below keep the league's clock; every other time on the site
      // is in the reader's own zone and named on the time (B-181).
      `The league keeps time in ${s.identity.timeZone.replace(/_/g, ' ')}: the rule times below are in that zone.`,
      s.identity.branding.trophyName !== 'League Champion'
        ? `The winner takes home the ${s.identity.branding.trophyName}.`
        : '',
    ].filter(Boolean),
  });

  const formatSentences = [`${FORMAT_NAMES[s.format.id]}.`];
  if (playsTheMedian(s))
    formatSentences.push('Each week you also play the league median for a second result.');
  if (playsTwoOpponents(s))
    formatSentences.push(
      'Every regular-season week you play two opponents, your one score against each, a result in each game.',
      // AN ODD LEAGUE rests two teams a game each week (Q6, B-614).
      ...(s.identity.teamCount % 2 === 1
        ? [`With ${s.identity.teamCount} teams, two of them play one game each week.`]
        : []),
    );
  if (s.format.elimination)
    formatSentences.push(
      `${plural(s.format.elimination.eliminatePerPeriod, 'team')} out each week until ${s.format.elimination.stopWhenTeamsRemain === 1 ? 'one is left' : `${s.format.elimination.stopWhenTeamsRemain} are left`}.`,
      // SAID BEFORE IT HAPPENS (B-364): a knockout cannot be taken back.
      `A knockout is final once the week is settled, even if a stat correction moves a score later, and the team's players go ${s.format.elimination.eliminatedRosterTo === 'free-agency' || s.waivers.system === 'none' ? 'straight to free agency' : 'to waivers'}.`,
    );
  sections.push({ id: 'format', title: 'Format', sentences: formatSentences });

  const scoring: string[] = [];
  scoring.push(`${table.heading}.`);
  scoring.push(
    ...(plainUnitSentences(sport, s.scoring.units) ?? unitSentences(sport, s.scoring.units)),
  );
  for (const b of s.scoring.bonuses)
    scoring.push(plainBonusSentence(sport, b) ?? bonusSentence(sport, b));
  for (const t of s.scoring.tiers)
    scoring.push(plainTierSentence(sport, t) ?? tierSentence(sport, t));
  scoring.push(
    s.scoring.decimals === 0
      ? 'Scores are whole points.'
      : `Scores keep ${s.scoring.decimals === 1 ? 'one decimal' : 'two decimals'}.`,
  );
  sections.push({ id: 'scoring', title: 'Scoring', sentences: scoring });

  const starters = s.roster.slots
    .filter(
      (slot) => (sport.slots.find((x) => x.id === slot.slot)?.kind ?? 'starter') === 'starter',
    )
    .filter((slot) => slot.count > 0)
    .map((slot) => `${slot.count} ${slotName(sport, slot.slot)}`);
  const bench = s.roster.slots
    .filter((slot) => sport.slots.find((x) => x.id === slot.slot)?.kind === 'bench')
    .reduce((sum, slot) => sum + slot.count, 0);
  const rosterSentences = [
    `Start ${list(starters)}.`,
    `${plural(bench, 'bench spot')}.`,
    s.roster.reserve && s.roster.reserve.count > 0 ? describeReserve(sport, s.roster.reserve) : '',
    // THE TAXI SQUAD AS THE LINEUP SAYS IT (PLAN-2026-10-27-the-next-season §2.7).
    s.roster.taxi && s.roster.taxi.count > 0
      ? describeTaxi({
          ...s.roster.taxi,
          deadlinePeriod: taxiDeadlineWeek(s.roster.taxi.deadlinePeriod, s.schedule.regularSeason),
          slotName: sport.slots.find((x) => x.id === s.roster.taxi!.slot)?.name ?? 'Taxi',
        })
      : '',
    ...Object.entries(s.roster.maxPerPosition).map(
      ([pos, max]) => `No more than ${max} ${positionNames(sport, [pos]) ?? pos}.`,
    ),
  ].filter(Boolean);
  sections.push({ id: 'roster', title: 'Roster', sentences: rosterSentences });

  const lineup: string[] = [];
  lineup.push(
    s.lineup.bestBall
      ? 'Best ball: your best lineup is set for you after the games.'
      : s.lineup.period === 'weekly'
        ? 'Set your lineup weekly.'
        : 'Set your lineup daily.',
  );
  lineup.push(
    s.lineup.lockRule === 'per-player-game'
      ? 'Each player locks when their game starts.'
      : s.lineup.lockRule === 'first-game'
        ? "The whole lineup locks at the week's first game."
        : `Lineups lock at ${s.lineup.fixedLockTime ? clock(s.lineup.fixedLockTime) : 'a fixed time'}.`,
  );
  for (const cap of s.lineup.gamesPlayedCaps)
    lineup.push(
      `${capitalize(positionNames(sport, cap.positions) ?? 'players')} can play at most ${plural(cap.maxGames, 'game')} per ${cap.per}.`,
    );
  if (!s.lineup.allowEmptySlots) lineup.push('Every starting slot must be filled.');
  sections.push({ id: 'lineup', title: 'Lineups', sentences: lineup });

  const schedule: string[] = [
    `Regular season runs weeks ${s.schedule.regularSeason.firstPeriod} through ${s.schedule.regularSeason.lastPeriod}.`,
  ];
  const divided = divisionSentences(s);
  if (divided.schedule !== null) schedule.push(divided.schedule);
  if (s.schedule.rivalryPeriods.length)
    schedule.push(`Rivalry weeks: ${list(s.schedule.rivalryPeriods.map(String))}.`);
  sections.push({ id: 'schedule', title: 'Schedule', sentences: schedule });

  const playoffs: string[] = [];
  if (s.playoffs.teamCount === 0) playoffs.push('No playoffs; the season decides it.');
  else {
    playoffs.push(
      `${plural(s.playoffs.teamCount, 'team')} make the playoffs${s.playoffs.byes ? `, the top ${plural(s.playoffs.byes, 'seed')} with a bye` : ''}.`,
    );
    if (s.playoffs.periods.length)
      playoffs.push(`Playoffs run weeks ${list(s.playoffs.periods.map(String))}.`);
    if (s.playoffs.championshipPeriods === 2)
      playoffs.push('The championship is a two-week matchup.');
    if (s.playoffs.reseed) playoffs.push('Brackets reseed each round.');
    if (divided.playoffs !== null) playoffs.push(divided.playoffs);
    if (s.playoffs.consolation !== 'none')
      playoffs.push(
        s.playoffs.consolation === 'toilet-bowl'
          ? 'The rest play a toilet bowl; nobody wants to win that one.'
          : 'The rest play a consolation bracket.',
      );
  }
  sections.push({ id: 'playoffs', title: 'Playoffs', sentences: playoffs });

  sections.push({
    id: 'tiebreakers',
    title: 'Tiebreakers',
    sentences: [
      `Standings ties go to ${list(s.tiebreakers.standings.map((t) => TIEBREAKER_NAMES[t]))}, in that order.`,
      s.tiebreakers.matchup === 'tie'
        ? 'A tied matchup is a tie.'
        : s.tiebreakers.matchup === 'bench-points'
          ? 'A tied matchup goes to bench points, and stays a tie if the benches are level too.'
          : s.tiebreakers.matchup === 'home-team'
            ? 'A tied matchup goes to the home team.'
            : 'A tied matchup goes to a coin flip.',
    ],
  });

  const waivers: string[] = [];
  if (s.waivers.system === 'none')
    waivers.push('No waivers: free agents are first come, first served.');
  else {
    waivers.push(
      s.waivers.system === 'faab'
        ? `Free-agent budget: $${s.waivers.faab?.budget ?? 0} for the season${s.waivers.faab?.minBid ? `, minimum bid $${s.waivers.faab.minBid}` : ''}.`
        : s.waivers.system === 'rolling'
          ? 'Rolling waiver priority: winning a claim sends you to the back of the line.'
          : 'Waiver priority follows reverse standings.',
    );
    const when =
      s.waivers.processing.schedule === 'continuous'
        ? 'Claims process as soon as the claim period ends'
        : s.waivers.processing.schedule === 'daily'
          ? `Claims process daily at ${clock(s.waivers.processing.time)}`
          : `Claims process ${WEEKDAYS[s.waivers.processing.weekday ?? 3]} at ${clock(s.waivers.processing.time)}`;
    waivers.push(`${when}.`);
    if (s.waivers.droppedPlayersGoToWaivers)
      waivers.push(
        `Dropped players sit on waivers for ${plural(s.waivers.claimPeriodHours, 'hour')}.`,
      );
    // The game-time lock after PLAN-2026-09-24 §2 rule 6 (B-142).
    if (s.waivers.gameTimeLock) waivers.push(...gameTimeLockSentences(s.waivers));
  }
  if (s.acquisitions.perPeriod !== null)
    waivers.push(`At most ${plural(s.acquisitions.perPeriod, 'pickup')} per week.`);
  if (s.acquisitions.perSeason !== null)
    waivers.push(`At most ${plural(s.acquisitions.perSeason, 'pickup')} per season.`);
  sections.push({ id: 'waivers', title: 'Waivers and free agents', sentences: waivers });

  const trades: string[] = [];
  trades.push(
    s.trades.review === 'none'
      ? 'Trades go through right away.'
      : s.trades.review === 'commissioner'
        ? `Trades are reviewed by the commissioner within ${plural(s.trades.reviewWindowHours, 'hour')}.`
        : `The league has ${plural(s.trades.reviewWindowHours, 'hour')} to vote a trade down${s.trades.vetoThreshold ? ` (${s.trades.vetoThreshold.kind === 'count' ? `${plural(s.trades.vetoThreshold.value, 'vote')} vetoes it` : `${Math.round(s.trades.vetoThreshold.value * 100)}% of the managers not in it veto it`})` : ''}.`,
  );
  // A trade with a bot is not reviewed (B-481), which a review rule says.
  if (s.trades.review !== 'none') trades.push('A trade with a bot goes through once it is agreed.');
  if (s.trades.deadlinePeriod !== null)
    trades.push(`Trade deadline: the end of Week ${s.trades.deadlinePeriod}.`);
  const assets = ['players'];
  if (s.trades.assets.draftPicks) assets.push('draft picks');
  if (s.trades.assets.faab) assets.push('free-agent budget');
  trades.push(`You can trade ${list(assets)}, up to ${s.trades.maxPlayersPerSide} per side.`);
  if (s.trades.allowDuringPlayoffs) trades.push('Trades are allowed during the playoffs.');
  sections.push({ id: 'trades', title: 'Trades', sentences: trades });

  // THE DRAFT, AS IT WILL HAPPEN: each mode says what it does and only the
  // rules it runs on (B-267). An autopick draft makes every pick at the draft
  // time, so a pick clock and what happens when it runs out are not its to
  // tell (B-122); a draft entered by hand has neither, nor a ranking.
  const draft: string[] = [];
  const d = s.draft;
  const orderName =
    d.order === 'linear'
      ? 'Linear draft (same order every round)'
      : d.order === 'third-round-reversal'
        ? 'Snake draft with third-round reversal'
        : 'Snake draft';
  const rounds = plural(d.rounds, 'round');
  const perPick =
    d.pickSeconds >= 3600
      ? plural(Math.round(d.pickSeconds / 3600), 'hour')
      : plural(d.pickSeconds, 'second');
  const auc = d.format === 'auction' ? d.auction : null;
  if (auc !== null) {
    // AN AUCTION SAYS ITS MONEY FIRST, then how it runs (AUCTION.md §4).
    const players = plural(d.rounds, 'player');
    const slow = auc.bidSeconds >= 3600;
    const span = (sec: number) =>
      sec >= 3600 ? plural(Math.round(sec / 3600), 'hour') : plural(sec, 'second');
    draft.push(
      d.mode === 'autopick'
        ? `Auction draft, $${String(auc.budget)} a team for ${players}, run at the draft time from each manager's prices.`
        : d.mode === 'manual'
          ? `Auction draft, $${String(auc.budget)} a team for ${players}, held in person and entered by the commissioner.`
          : `${slow ? 'Slow auction' : 'Auction'} draft, $${String(auc.budget)} a team for ${players}${auc.lotsAtOnce > 1 ? `, ${String(auc.lotsAtOnce)} players up at once` : ''}.`,
    );
    if (d.mode === 'live')
      draft.push(
        `${span(auc.nominationSeconds)} to nominate and ${span(auc.bidSeconds)} to bid; a bid with under ${span(auc.resetSeconds)} left puts the clock back to ${span(auc.resetSeconds)}.`,
      );
    draft.push(
      `The most a team can bid keeps $${String(auc.minBid)} for each empty spot after the player.`,
    );
    draft.push(
      auc.leftoverToWaivers
        ? 'Money left over joins the waiver budget.'
        : 'Money left over is gone when the draft ends.',
    );
  } else if (d.mode === 'autopick')
    draft.push(`${orderName}, ${rounds}, every pick made at the draft time.`);
  else if (d.mode === 'manual')
    draft.push(`${orderName}, ${rounds}, drafted in person and entered by the commissioner.`);
  else draft.push(`${orderName}, ${rounds}, ${perPick} per pick.`);
  // Second, so the create form's review, which shows the first three, says it.
  if (d.mode === 'autopick' && auc === null)
    draft.push(
      d.ranking === 'by-position'
        ? 'Managers rank players by position, and choose which position each round takes.'
        : 'Managers rank players in one list, with a fewest and a most at each position.',
    );
  if (d.scheduledAt) {
    const at = new Date(d.scheduledAt);
    const whenText = Number.isNaN(at.getTime())
      ? null
      : at.toLocaleString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZone: s.identity.timeZone,
        });
    if (whenText) draft.push(`Draft day: ${whenText}.`);
  } else draft.push('Draft not scheduled yet.');
  draft.push(
    d.mode === 'manual'
      ? 'The commissioner enters the draft order with the picks.'
      : d.seatOrder === 'random'
        ? 'Draft order is randomized before the draft.'
        : d.seatOrder === 'manual'
          ? 'The commissioner sets the draft order.'
          : 'Draft order is last season’s finish reversed: last place picks first and the champion last.',
  );
  if (d.mode === 'live') {
    draft.push(
      auc !== null
        ? d.whenTheClockExpires === 'off'
          ? 'If your nomination clock runs out, the room waits for you.'
          : 'If your nomination clock runs out, the room puts up the first player in your queue, else the highest-valued player, at the lowest bid.'
        : d.whenTheClockExpires === 'queue-then-rankings'
          ? 'If your clock runs out, the room picks for you: your queue, then your priorities, then the board.'
          : d.whenTheClockExpires === 'rankings'
            ? 'If your clock runs out, the room picks for you from your priorities, then the board.'
            : 'If your clock runs out, the room waits for your pick.',
    );
    if (d.overnightPause)
      draft.push(
        `The clock pauses overnight from ${clock(d.overnightPause.from)} to ${clock(d.overnightPause.to)}.`,
      );
    if (d.pickTradingAllowed)
      draft.push('Managers can trade picks and drafted players with each other during the draft.');
  }
  sections.push({ id: 'draft', title: 'Draft', sentences: draft });

  const keepers: string[] = [];
  if (s.keepers.mode === 'redraft') keepers.push('Redraft: everyone starts fresh each season.');
  else if (s.keepers.mode === 'dynasty') {
    // PLAN-2026-10-27-the-next-season §2.4, said as it plays.
    keepers.push('Dynasty: every team keeps its whole roster from one season to the next.');
    keepers.push(
      s.keepers.rookieDraft
        ? 'After the first season, the draft takes only rookies, and a team over its roster’s size lets players go before it can add one.'
        : 'After the first season, the draft takes any player on no team, and a team over its roster’s size lets players go before it can add one.',
    );
  } else {
    // PLAN-2026-10-27-the-next-season §2.3, said as it plays.
    keepers.push(
      `Keeper league: each team keeps up to ${plural(s.keepers.keeperCount, 'player')} from last season's roster${s.keepers.maxYearsKept !== null ? `, each for at most ${plural(s.keepers.maxYearsKept, 'season')} in a row` : ''}, chosen by an hour before the draft.`,
    );
    const auction = d.format === 'auction';
    keepers.push(
      s.keepers.cost === 'draft-round'
        ? 'A keeper costs the round he was drafted in last season, and a player nobody drafted costs the last round.'
        : s.keepers.cost === 'draft-round-escalating'
          ? `A keeper costs the round he was drafted in, ${plural(s.keepers.escalationRounds, 'round')} earlier for each season he is kept; one whose cost would be before the first round cannot be kept.`
          : s.keepers.cost === 'auction-value'
            ? 'A keeper costs what he went for in last season’s auction, and a player nobody bought costs the lowest bid.'
            : auction
              ? 'A keeper costs nothing from the budget and takes one of the team’s roster spots.'
              : 'A keeper takes one of the team’s picks, its earliest rounds first.',
    );
  }
  // PICKS AHEAD, where picks trade (PLAN-2026-10-27-the-next-season §2.6).
  if (s.keepers.mode !== 'redraft' && s.trades.assets.draftPicks) {
    if (s.keepers.futurePickTrading)
      keepers.push(
        `Draft picks up to ${plural(s.keepers.futurePickTrading, 'season')} ahead can be traded, as well as the coming draft’s.`,
      );
  }
  sections.push({ id: 'keepers', title: 'Keepers', sentences: keepers });

  const governance: string[] = [
    s.governance.changesRequire === 'commissioner'
      ? 'The commissioner can change the rules; every change is logged for everyone.'
      : // B6: a vote binds once the league has drafted; before it, the
        // commissioner is still setting the league up.
        s.governance.changesRequire === 'majority-vote'
        ? 'After the draft, rule changes need a yes from a majority of the managers.'
        : `After the draft, rule changes need a yes from ${Math.round(s.governance.supermajorityFraction * 100)}% of the managers.`,
  ];
  if (s.governance.lockedAfterDraft.length)
    governance.push(`After the draft, ${list(s.governance.lockedAfterDraft)} are locked.`);
  governance.push(
    s.governance.scoringChangesApply === 'future-periods'
      ? 'Scoring changes apply from the next week; nothing already scored changes.'
      : 'Scoring changes apply to the whole season.',
  );
  sections.push({ id: 'governance', title: 'Rule changes', sentences: governance });

  const a = s.fun.leagueAwards;
  const ach = s.fun.achievements;
  const fun: string[] = [];
  if (a.perPeriod)
    fun.push(
      a.postToChat
        ? 'Weekly league awards are handed out when each week ends and posted to chat.'
        : 'Weekly league awards are handed out when each week ends.',
    );
  else fun.push('No weekly league awards.');
  fun.push(a.perSeason ? 'Season league awards are on.' : 'Season league awards are off.');
  // Achievements get their own sentence now: they are account-scoped, they are
  // earned rather than won, and since schema version 2 they have their own
  // chat switch. One sentence covering both read as though an achievement were
  // a kind of award (B-111).
  fun.push(
    !ach.enabled
      ? 'Account achievements are off in this league.'
      : ach.postToChat
        ? 'Account achievements are tracked, and unlocks post to chat.'
        : 'Account achievements are tracked quietly; unlocks do not post to chat.',
  );
  // The weekly hat (PLAN-2026-10-30-the-weekly-hat §6).
  fun.push(
    s.fun.hat.enabled
      ? 'The weekly hat: the team with the fewest points each week wears a hat the week’s high scorer picks, until the next week’s.'
      : 'No weekly hat.',
  );
  if (a.disabled.length)
    fun.push(`Turned off: ${list(a.disabled.map((id) => a.names[id] ?? id.replace(/-/g, ' ')))}.`);
  for (const [id, name] of Object.entries(a.names))
    fun.push(`The ${id.replace(/-/g, ' ')} award is called ${name} here.`);
  sections.push({ id: 'fun', title: 'Fun, awards and achievements', sentences: fun });

  // The switches in force (`effectiveTools`): the trade read-out and the
  // lineup total stand on projections, and are said off with them.
  const t = effectiveTools(s.tools);
  const tools: string[] = [];
  if (t.projections && t.suggestions && t.tradeAnalysis && t.sandbox)
    tools.push(
      'Every helper is on: projected points, Worth a look and trending, the trade read-out and the lineup’s projected total.',
    );
  else {
    if (!t.projections)
      tools.push(
        'Projected points are off, and so are the trade read-out and the lineup’s projected total, which are built from them.',
      );
    if (!t.suggestions)
      tools.push('Worth a look, trending players and the list of trade partners are off.');
    if (t.projections && !t.tradeAnalysis) tools.push('The trade read-out is off.');
    if (t.projections && !t.sandbox) tools.push('The lineup’s projected total is off.');
    tools.push('Scores and stats are not affected.');
  }
  sections.push({ id: 'tools', title: 'Helper tools', sentences: tools });

  return { headline, sections };
}
